/* eslint-disable @typescript-eslint/no-namespace */
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { default as relativeTimePlugin } from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
/**
 * A custom dayjs plugin. See https://day.js.org/docs/en/plugin/plugin
 * for additional information on how custom dayJS plugins are built and
 * instantiated.
 * @param  {unknown} option
 * @param  {typeof Dayjs} dayjsClass
 */
const hvDatePlugin = (option, dayjsClass) => {
    const oldFormat = dayjsClass.prototype.format;
    /**
     * The primary date formatting function at Healthvana.
     * @method
     * @param  {FormatOptions} template A date format.
     * - 'ISO' - ISO-8601 (no timestamp, date only): 2021-02-25
     * - 'MES' - Middle-endian slash: 02/25/2021
     * - 'DATE-WORDS' - Written out: Saturday, February 25th 2021
     * - 'DATE-WORDS-NO-YEAR' - Written out: Saturday, February 25th
     * - 'MES-TIME-TZ' - MES with time and timezone: 02/25/2021 3:15 PM CST
     * - 'TIME-TZ' - Only time (no date) and timezone: 3:15 PM CST
     * - 'RELATIVE' - uses relativeTime function, see above for docs
     * @param  {string} customTemplate? Only used if 'CUSTOM' is passed
     * as a template format.
     *
     * @returns string The date formatted correctly per the provided format.
     */
    dayjsClass.prototype.hvFormat = function (template, customTemplate) {
        const formatStrings = {
            MES: 'MM/DD/YYYY',
            ISO: 'YYYY-MM-DD',
            'MES-TIME-TZ': 'MM/DD/YYYY h:mm A z',
            'TIME-TZ': 'h:mm A z',
            'DATE-WORDS': 'dddd, MMMM Do YYYY',
            'DATE-WORDS-NO-YEAR': 'dddd, MMMM Do',
            'hh:mm': 'hh:mm',
            a: 'a'
        };
        if (template === 'RELATIVE')
            return relativeTime(this);
        if (template === 'CUSTOM')
            return oldFormat.bind(this)(customTemplate);
        return oldFormat.bind(this)(formatStrings[template]);
    };
};
const relativeTimeDefaults = {
    relativeTime: {
        future: 'in %s',
        past: '%s ago',
        s: 'a few seconds',
        m: 'a minute',
        mm: '%d min',
        h: 'an hour',
        hh: '%d hours',
        d: 'a day',
        dd: '%d days',
        M: 'a month',
        MM: '%d months',
        y: '%d year',
        yy: '%d years'
    }
};
dayjs.extend(advancedFormat); // required for ordinals
dayjs.extend(localizedFormat); // required for localized dates
dayjs.extend(relativeTimePlugin); // required for relative time
dayjs.extend(timezone);
dayjs.extend(updateLocale); // required for customizing relative time
dayjs.extend(utc); // required for timezone plugin to work
dayjs.extend(hvDatePlugin); // our custom plugin, see above
dayjs.updateLocale('en', relativeTimeDefaults);
dayjs.extend(customParseFormat);
/**
 * Shows a date as a relative date or time.
 * If the date is less than an hour from now, shows time delta in minutes.
 * If more than an hour ago but still within the current calendar year,
 * shows Month and Day.
 * If older than the current calendar year, shows M/D/YYYY format.
 * @param  {DateInput} date?
 * @param  {DateInput} now?
 * @param  {DateInput} daysCutOff? amount of days after which to show actual date instead of "X days ago"
 * @returns string | null
 */
export const relativeTime = (date, now, daysCutOff) => {
    if (!date)
        return '';
    // Make both arguments dayjs format
    date = dayjs(date);
    now = now ? dayjs(now) : dayjs(Date.now());
    // Is it less than 1 hour ago, or older than a year?
    const lessThanHourAgo = date.isAfter(now.subtract(1, 'hour'));
    const after1Year = now.year() > date.year();
    if (lessThanHourAgo)
        return date.from(now);
    if (after1Year)
        return dayjs(date).format('l');
    if (daysCutOff) {
        // Calculate the difference in days
        const daysAgo = now.diff(date, 'day');
        // If within the last X days, display "X days ago"
        if (daysAgo <= daysCutOff) {
            return date.from(now);
        }
    }
    return dayjs(date).format('MMM D');
};
function isValidTimezone(tzString) {
    try {
        dayjs().tz(tzString);
        return true;
    }
    catch (e) {
        return false;
    }
}
/**
 * A lightweight wrapper around Dayjs. Returns a Dayjs object, or null if an invalid date is passed in. Combined with hvDatePlugin, can be used to format dates in
 * the approved Healthvana formats.
 * @param  {DateInput} value?
 * @param  {string} timezone?
 * @returns Dayjs | null
 */
export const hvDate = (value, timezone) => {
    if (value === undefined || value === 'null' || !value)
        return null;
    let date;
    // Guess timezone if we don't have it passed in
    if (!timezone || !isValidTimezone(timezone))
        timezone = dayjs.tz.guess();
    if (value === 'now') {
        date = dayjs().tz(timezone);
    }
    else {
        date = dayjs(value).tz(timezone);
    }
    // TODO - we should just reformat the date here, instead of warning to fix it before passing into hvDate
    if (typeof value == 'string' && (value[2] == '/' || value[2] == '-')) {
        // MM-DD-YYYY will work on chrome, but will not work on safari or firefox
        console.warn('enter date as YYYY-MM-DD to work across browsers');
    }
    if (!date.isValid()) {
        console.error(`Invalid date passed to hvDate: ${value}`);
        return null;
    }
    return date;
};
/**
 * hvUTC converts a date and time string with a specified meridiem and timezone into a UTC date string.
 *
 * @param {string} dateString - The date string in 'MM/DD/YYYY' format.
 * @param {string} timeString - The time string in 'hh:mm' format.
 * @param {string} meridiem - The meridiem string, either 'am' or 'pm'.
 * @param {string} timezone - The timezone name, e.g., 'US/Pacific'.
 * @returns {string | null} The formatted UTC date string in 'YYYY-MM-DDTHH:mm:ssZ' format, or null if the input is invalid.
 *
 * @example
 * const dateString = '05/22/2024';
 * const checkin_time = '09:44';
 * const checkin_time_meridiem = 'am';
 * const timezone = 'US/Pacific';
 * const finalDate = hvUTC(dateString, checkin_time, checkin_time_meridiem, timezone);
 * returns '2024-05-22T16:44:00Z'
 * */
export const hvUTC = (dateString, timeString, meridiem, timezone) => {
    const inputFormat = 'MM/DD/YYYY hh:mm a';
    const outputFormat = 'YYYY-MM-DDTHH:mm:ss';
    const datetimeString = `${dateString} ${timeString} ${meridiem}`;
    // Parse the datetime string, using strict parsing format to handle the date and time together https://day.js.org/docs/en/parse/string-format encountered parsing issues otherwise
    const parsedDatetime = dayjs(datetimeString, inputFormat, true);
    // Validate parsed datetime
    if (!parsedDatetime.isValid()) {
        console.error('Invalid date format');
        return null;
    }
    else {
        const formattedDateTime = parsedDatetime.format(outputFormat);
        // Convert datetime to UTC and format
        const finalDateInUTC = dayjs
            .tz(formattedDateTime, outputFormat, timezone)
            .utc()
            .format();
        return finalDateInUTC;
    }
};
/**
 * See: https://day.js.org/docs/en/manipulate/manipulate
 * Modifies a given date by adding or subtracting a specified quantity of units.
 * Uses Dayjs library for date manipulation to ensure cross-browser consistency.
 * Uses UTC to avoid timezone issues.
 *
 * @param {string} date - The initial date in 'YYYY-MM-DD' format.
 * @param {Object} change - An object describing the modification to apply.
 *   @property {number} qty - The quantity of units to add or subtract.
 *   @property {string} operator - The operation to perform: 'add' or 'subtract'.
 *   @property {any} unit - The unit of time to add or subtract (e.g., 'd' for days, 'h' for hours).
 *   @property {string} format - The format to return the date in to be passed to hvFormat.
 *
 * @returns {string} The formatted, modified date.
 *
 * @example
 * const date = '2023-08-03';
 * const yesterday = modifyDateByUnit(date, { qty: 1, unit: 'd', operator: 'subtract', format: 'YYYY-MM-DD' });
 * Output: '2023-08-02'
 *
 * @example
 * const date = '2023-08-03';
 * const nextWeek = modifyDateByUnit(date, { qty: 1, unit: 'w', operator: 'add', format: 'YYYY-MM-DD' });
 * Output: '2023-08-10'
 *
 */
export const manipulateDate = (date = new Date(), options) => {
    const { amount, operator, unit, format } = options;
    const newDate = dayjs.utc(new Date(date))[operator](amount, unit);
    return newDate.hvFormat(format);
};
export const disableDateRange = ({ targetDate = 'now', startRangeAmount = 0, startRangeUnit = 'day', endRangeAmount = 0, endRangeUnit = 'day' } = {}) => {
    const target = hvDate(targetDate);
    const today = hvDate('now');
    const todayValue = today === null || today === void 0 ? void 0 : today.endOf('day').valueOf();
    // Extra guard against invalid dates
    if (!target || !todayValue)
        return;
    // Subtract the start range from the target date and get value in milliseconds
    const startRangeValue = target
        .subtract(startRangeAmount, startRangeUnit)
        .endOf('day')
        .valueOf();
    // Add the end range to the target date and get value in milliseconds
    const endRangeValue = target
        .add(endRangeAmount, endRangeUnit)
        .endOf('day')
        .valueOf();
    // This func runs for each date in the DatePicker calendar +/- ~1 month from target date.
    // If it returns true, that date becomes disabled.
    return (current) => {
        if (!current)
            return false;
        const currentValue = current.endOf('day').valueOf();
        // If no end range provided, disable all dates before and including today. Assumes we'll never need to schedule anything in the past.
        if (!endRangeAmount) {
            return currentValue <= todayValue;
        }
        // Otherwise disable dates outside of start/end range, and today.
        // Note that 'current' is a Moment object and today/endRange/startRange are DayJS objects,
        // but we can compare their numeric unix value in milliseconds.
        return (currentValue < startRangeValue ||
            currentValue > endRangeValue ||
            currentValue <= todayValue);
    };
};
